import React, { useEffect, useRef } from 'react';

interface ParticleWaveProps {
    interactive?: boolean;
}

export const ParticleWave: React.FC<ParticleWaveProps> = ({ interactive = true }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let width = canvas.width = window.innerWidth;
        let height = canvas.height = window.innerHeight;

        let particles: Particle[] = [];
        const gap = 35;
        const rows = Math.ceil(height / gap);
        const cols = Math.ceil(width / gap);

        let mouseX = -1000;
        let mouseY = -1000;
        let scrollY = 0;
        let time = 0;
        let animationFrameId: number;
        let isVisible = true; // Flag to track visibility

        class Particle {
            x: number;
            y: number;
            originX: number;
            originY: number;
            size: number;
            baseSize: number;
            angle: number;
            distanceToMouse: number;

            constructor(x: number, y: number) {
                this.x = x;
                this.y = y;
                this.originX = x;
                this.originY = y;
                this.baseSize = 1.0;
                this.size = this.baseSize;
                this.angle = (x + y) * 0.002;
                this.distanceToMouse = 1000;
            }

            update() {
                // 1. WAVES (Always active but subtle)
                this.angle += 0.015;
                const waveY = Math.sin(this.angle) * 10;

                // 2. HEARTBEAT (Always pulsatng)
                const heartbeat = Math.sin(time * 0.04) * 0.6 + 0.6; // 0.2 to 1.2

                // 3. MOUSE INTERACTION (Only if interactive mode AND away from edges)
                if (interactive) {
                    // Edge detection: 50px buffer
                    const isNearEdge = mouseX < 50 || mouseX > width - 50 || mouseY < 50 || mouseY > height - 50;

                    const dx = mouseX - this.x;
                    const dy = mouseY - this.y;
                    this.distanceToMouse = Math.sqrt(dx * dx + dy * dy);
                    const maxDistance = 350; // Large radius

                    let force = 0;

                    if (!isNearEdge && this.distanceToMouse < maxDistance) {
                        force = (maxDistance - this.distanceToMouse) / maxDistance;
                        const forceDirectionX = dx / this.distanceToMouse;
                        const forceDirectionY = dy / this.distanceToMouse;

                        // Repulsion (Magnet effect - pushing HOLES open / pushing dots away to reveal light)
                        // Pushing dots AWAY expands the space between them.
                        const repulsion = 70;
                        this.x = this.originX - forceDirectionX * force * repulsion;
                        this.y = this.originY + waveY - forceDirectionY * force * repulsion;

                        // "Hole Expansion" -> Particle gets bigger to simulate light bleeding through?
                        // Or particle gets smaller?
                        // User said "rupice prošire". If particles represent the solid "mesh", they should technically SHRINK to make holes bigger?
                        // But usually in canvas dots = light. 
                        // Let's assume DOTS = LIGHT SOURCES (the holes). So they should GROW.
                        this.size = this.baseSize + (force * 3) + heartbeat;
                    } else {
                        // Return to origin
                        this.x += (this.originX - this.x) * 0.08;
                        this.y += (this.originY + waveY - this.y) * 0.08;
                        this.size = this.baseSize + (heartbeat * 0.5);
                    }
                } else {
                    // Non-interactive mode: only waves and heartbeat
                    this.x += (this.originX - this.x) * 0.08;
                    this.y += (this.originY + waveY - this.y) * 0.08;
                    this.size = this.baseSize + (heartbeat * 0.5);
                    this.distanceToMouse = 1000; // Always far from mouse
                }
            }

            draw() {
                if (!ctx) return;

                // FADE OUT ON SCROLL logic (ONLY for interactive mode)
                let scrollOpacity = 1;
                if (interactive) {
                    // Max scroll fade at 800px
                    scrollOpacity = Math.max(0, 1 - (scrollY / 800));
                    if (scrollOpacity <= 0.01) return; // Optimization
                }

                ctx.globalAlpha = scrollOpacity;

                // BEAM PHYSICS
                // Direction: From Mouse TO Particle (Radial outward)
                if (this.distanceToMouse < 250 && scrollOpacity > 0.5) {
                    const intensity = 1 - (this.distanceToMouse / 250);

                    // Calculate angle from mouse to particle
                    const angle = Math.atan2(this.y - mouseY, this.x - mouseX);

                    // Beam length
                    const beamLength = 150 * intensity;

                    // End point of beam (radiating outwards)
                    const endX = this.x + Math.cos(angle) * beamLength;
                    const endY = this.y + Math.sin(angle) * beamLength;

                    // Draw Radial Beam
                    if (intensity > 0.1) {
                        const gradient = ctx.createLinearGradient(this.x, this.y, endX, endY);
                        gradient.addColorStop(0, `rgba(165, 180, 252, ${intensity * 0.9})`); // Bright Indigo start
                        gradient.addColorStop(1, 'transparent'); // Fade out

                        ctx.fillStyle = gradient;

                        // Trapezoid shape for beam (wider at end?)
                        // Simple line with width is faster/cleaner

                        ctx.beginPath();
                        // Perpendicular offset for thickness
                        const perpAngle = angle + Math.PI / 2;
                        const thickness = this.size * 1.5;

                        ctx.moveTo(this.x + Math.cos(perpAngle) * thickness, this.y + Math.sin(perpAngle) * thickness);
                        ctx.lineTo(endX + Math.cos(perpAngle) * (thickness * 4), endY + Math.sin(perpAngle) * (thickness * 4)); // Fans out
                        ctx.lineTo(endX - Math.cos(perpAngle) * (thickness * 4), endY - Math.sin(perpAngle) * (thickness * 4));
                        ctx.lineTo(this.x - Math.cos(perpAngle) * thickness, this.y - Math.sin(perpAngle) * thickness);

                        ctx.closePath();
                        ctx.fill();
                    }

                    // Bright core
                    ctx.fillStyle = `rgba(255, 255, 255, ${0.8 + intensity * 0.2})`;
                    ctx.shadowBlur = 20 * intensity;
                    ctx.shadowColor = 'rgba(165, 180, 252, 0.9)';
                } else {
                    // Idle state - brighter and pulsing
                    // We can derive pulse from size since size = base + heartbeat*0.5
                    // heartbeat ~ (size - base) * 2
                    const pulse = (this.size - this.baseSize) * 2;
                    // Opacity ranges roughly 0.4 to 0.8
                    const alpha = 0.4 + (pulse * 0.3);

                    ctx.shadowBlur = 0;
                    ctx.fillStyle = `rgba(148, 163, 184, ${alpha})`; // Slate-400, brighter
                }

                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fill();

                ctx.shadowBlur = 0;
                ctx.globalAlpha = 1; // Reset opacity
            }
        }

        const initParticles = () => {
            particles = [];
            const rows = Math.ceil(height / gap);
            const cols = Math.ceil(width / gap);
            for (let i = 0; i < cols; i++) {
                for (let j = 0; j < rows; j++) {
                    particles.push(new Particle(i * gap + gap / 2, j * gap + gap / 2));
                }
            }
        };
        initParticles();

        const animate = () => {
            if (!isVisible) return; // Stop if not visible

            time++;
            ctx.clearRect(0, 0, width, height);

            particles.forEach(p => {
                p.update();
                p.draw();
            });
            animationFrameId = requestAnimationFrame(animate);
        };

        // Start animation loop
        animate();

        const handleResize = () => {
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
            initParticles();
        };

        const handleMouseMove = (e: MouseEvent) => {
            mouseX = e.clientX;
            mouseY = e.clientY;
        };

        const handleScroll = () => {
            scrollY = window.scrollY;
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('scroll', handleScroll);

        // Intersection Observer to pause animation when off-screen
        const observer = new IntersectionObserver(([entry]) => {
            isVisible = entry.isIntersecting;
            if (isVisible) {
                // Restart only if stopped
                if (!animationFrameId) {
                    animate();
                } else {
                    // actually if we just set isVisible=true, the *next* requestAnimationFrame call might have been cancelled?
                    // No, if isVisible was false, requestAnimationFrame wasn't called.
                    // So we need to kickstart it.
                    // But "animate" checks isVisible immediately.
                    // Let's ensure we don't double start.
                    cancelAnimationFrame(animationFrameId); // Safety
                    animate();
                }
            } else {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                }
            }
        }, { threshold: 0 });

        observer.observe(canvas);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas ref={canvasRef} className="block w-full h-full" />
    );
};
